import { XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import React, { useCallback } from "react";
import ReactModal from "react-modal";
import Button from "../form/Button";

type ModalProps = {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  removeClose?: boolean;
  onRequestClose: () => void;
  isOpen: boolean;
  noTransition?: boolean;
  width?: number | string;
  maxWidth?: number | string;
  minWidth?: number | string;
  disableScroll?: boolean;
  background?:
    | "bg-white"
    | "bg-athens-gray"
    | "bg-gray-100"
    | "bg-gray-50"
    | "bg-transparent";
  style?: React.CSSProperties;
  shouldCloseOnOverlayClick?: boolean;
};

const Modal: React.FC<ModalProps> = ({
  children,
  className,
  containerClassName,
  removeClose = false,
  onRequestClose,
  noTransition,
  width = 400,
  maxWidth = 800,
  minWidth = 400,
  disableScroll,
  background = "bg-white",
  style,
  shouldCloseOnOverlayClick,
  ...props
}) => {
  const handleRequestClose = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      onRequestClose();
    },
    [onRequestClose]
  );

  return (
    <ReactModal
      style={{
        content: { width, maxWidth, minWidth, ...style },
        overlay: {
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          margin: "auto",
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9000,
        },
      }}
      overlayClassName={classNames("ReactModal__Overlay no-scrollbar", {
        ReactModal__Overlay__Transition: !noTransition,
      })}
      className={classNames(
        "max-h-full outline-none no-scrollbar",
        { "overflow-hidden": disableScroll, "overflow-auto": !disableScroll },
        containerClassName
      )}
      onRequestClose={handleRequestClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick || false}
      aria-modal="true"
      {...props}
    >
      <div
        className={classNames(
          `flex flex-col justify-center ${background} rounded-md relative`,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
        {!removeClose && (
          <Button
            variant="icon"
            onClick={handleRequestClose}
            className="absolute top-4 right-4 w-8 h-8 text-gray-400 flex items-center justify-center"
            aria-label="Close"
          >
            <XMarkIcon className="w-4" />
          </Button>
        )}
      </div>
    </ReactModal>
  );
};

export default Modal;
